import Control from '../../1. Core/Control';
import DataTable from '../../1. Core/Controls/DataTable/DataTable';
import TopAppBar from '../../1. Core/Controls/TopAppBar/TopAppBar';
import Delegate from '../../1. Core/Delegate';
import IconToggleButton from '../../1. Core/Controls/IconToggleButton/IconToggleButton';
import Menu from '../../1. Core/Controls/Menu/Menu';
import ListItem from '../../1. Core/Controls/ListBox/ListItem';
import Dialog from '../../1. Core/Controls/Dialog/Dialog';
import NP from '../../1. Core/NP';
import {ServerAPI} from '../../1. Core/ServerAPI';
import SimpleAthletesList from '../../2. Athletes/SimpleAthletesList';
import {IAthleteListItem} from '../../2. Athletes/AthletesList';
import {PageMode} from '../../10. Battle/types';
import Select from '../../1. Core/Controls/Select/Select';

export interface SiteRankingBoard {
	slug: string;
	titleEn: string;
	titleRu: string;
	kind: string;
	weightClassKey?: string;
	editable: boolean;
	maxEntries?: number | null;
	entryCount?: number;
}

interface CatalogRow {
	catalogId: number;
	athleteId?: number;
	fullName: string;
	displayOrder: number;
	isChampion: boolean;
	active: boolean;
	photoUrl?: string;
	weightClassKey?: string;
	championLabel?: string;
	activeLabel?: string;
	weightClassLabel?: string;
}

interface BoardRow {
	entryId: number;
	athleteId: number;
	fullName: string;
	displayOrder: number;
	photoUrl?: string;
	championLabel?: string;
	activeLabel?: string;
}

type RankRow = CatalogRow | BoardRow;

export default class SiteUfcRankingsBoardPanel extends Control {
	private _topAppBar: TopAppBar;
	private _limitHint: HTMLDivElement;
	private _dataTable: DataTable;
	private _Board: SiteRankingBoard;
	private EvBackClick: Delegate;
	private _rows: RankRow[] = [];
	private _rootNode: HTMLElement;

	constructor(settings: object) {
		super(settings);
		this._render();
	}

	_initFields(settings: object) {
		this._Board = null;
		this.EvBackClick = new Delegate();
		super._initFields(settings);
	}

	getTopAppBar() {
		return this._topAppBar;
	}

	isChampionsBoard(): boolean {
		return this._Board?.kind === 'champions';
	}

	isPfpBoard(): boolean {
		const kind = this._Board?.kind || '';
		return kind.startsWith('pfp_');
	}

	isBoardFull(): boolean {
		const board = this._Board;
		if (!board || board.maxEntries == null) {
			return false;
		}
		return (board.entryCount ?? this._rows.length) >= board.maxEntries;
	}

	/** athletes.Sex: 1 = male, 0 = female */
	requiredBoardSex(): number | null {
		const board = this._Board;
		if (!board || this.isChampionsBoard()) {
			return null;
		}
		if (board.kind === 'pfp_men') {
			return 1;
		}
		if (board.kind === 'pfp_women') {
			return 0;
		}
		if (board.kind === 'pfp_all') {
			return null;
		}
		if (board.kind === 'division') {
			return board.weightClassKey?.startsWith("Women's ") ? 0 : 1;
		}
		return null;
	}

	_render() {
		this.np_render();
		this._rootNode = NP.createNode(this._root);

		this._topAppBar = new TopAppBar({
			Class: 'top-app-bar--no-shadow top-app-bar--light library-box-list__app-bar',
			Caption: this._Board?.titleEn || 'Rankings',
			ParentNode: this._rootNode,
			ShowSearchButton: false,
			RightItems: NP.getCurrentUser().isBDAdmin() && !this.isChampionsBoard()
				? [
					new IconToggleButton({
						FontImage: NP.FontImages.add,
						ToolTip: 'Add fighter',
						EvClick: () => this.addFighter(),
					}),
				]
				: [],
			EvBackClick: () => this.EvBackClick.fire(this),
		});
		this._topAppBar.setShowBackButton(true);

		this._limitHint = document.createElement('div');
		this._limitHint.className = 'site-rankings-limit-hint';
		this._rootNode.appendChild(this._limitHint);

		const columns = this.isChampionsBoard()
			? [
				{Content: 'Name', PropertyContent: 'fullName', Width: '34%'},
				{Content: 'Weight class', PropertyContent: 'weightClassLabel', Width: '26%'},
				{Content: 'Order', PropertyContent: 'displayOrder', Width: '10%'},
				{Content: 'Athlete', PropertyContent: 'athleteId', Width: '12%'},
			]
			: [
				{Content: 'Name', PropertyContent: 'fullName', Width: '30%'},
				{Content: 'Order', PropertyContent: 'displayOrder', Width: '10%'},
				{Content: 'Champion', PropertyContent: 'championLabel', Width: '12%'},
				{Content: 'Active', PropertyContent: 'activeLabel', Width: '10%'},
				{Content: 'Athlete', PropertyContent: 'athleteId', Width: '10%'},
			];

		this._dataTable = new DataTable({
			ParentNode: this._rootNode,
			ShowSettingsButton: false,
			Class: 'app-list--avatars mdc-data-table--margin data-table-battle',
			MultiSelect: false,
			CanDragRows: !this.isChampionsBoard(),
			ShowRowIndex: true,
			EnableSort: false,
			ImageWidth: 40,
			ImageProperty: 'photoUrl',
			Columns: columns,
			ShowPaginator: false,
			EvRowMoved: async (s: object, e: { Row: RankRow; Index: number }) => {
				await this.moveRowToIndex(e.Row, e.Index);
			},
			EvItemContextMenu: async (s: object, e: { Event: Event; Item: RankRow }) => {
				if (!NP.getCurrentUser().isBDAdmin() || this.isChampionsBoard()) {
					return;
				}
				const rowIndex = this.rowIndexOf(e.Item);
				this.showContextMenu(e.Event, e.Item, rowIndex);
			},
		});

		this.refresh();
	}

	updateLimitHint() {
		const board = this._Board;
		if (!board) {
			this._limitHint.textContent = '';
			return;
		}
		if (this.isChampionsBoard()) {
			this._limitHint.textContent =
				'Champions are edited on division boards — use Set champion on a weight-class entry.';
			return;
		}
		const count = board.entryCount ?? this._rows.length;
		if (board.maxEntries != null) {
			const full = count >= board.maxEntries;
			this._limitHint.textContent = full
				? `${count} / ${board.maxEntries} — board full; when adding, choose who to replace`
				: `${count} / ${board.maxEntries} fighters (UFC top-${board.maxEntries})`;
			return;
		}
		this._limitHint.textContent = `${count} fighters`;
	}

	rowIndexOf(row: RankRow): number {
		if ('catalogId' in row) {
			return this._rows.findIndex(r => 'catalogId' in r && r.catalogId === row.catalogId);
		}
		return this._rows.findIndex(r => 'entryId' in r && r.entryId === row.entryId);
	}

	async refresh() {
		const board = this._Board;
		if (!board) {
			this._rows = [];
			this._dataTable.setRows([]);
			this.updateLimitHint();
			return;
		}

		if (this.isChampionsBoard()) {
			const rows = await ServerAPI.get<CatalogRow[]>('/site/rankings/champions');
			this._rows = rows.map(r => ({
				...r,
				championLabel: 'C',
				activeLabel: 'Yes',
				weightClassLabel: r.weightClassKey || '',
			}));
			this._dataTable.setRows(this._rows);
			board.entryCount = this._rows.length;
			this.updateLimitHint();
			return;
		}

		if (this.isPfpBoard()) {
			const rows = await ServerAPI.get<BoardRow[]>(
				`/site/rankings/board?boardSlug=${encodeURIComponent(board.slug)}`,
			);
			this._rows = rows.map(r => ({...r, championLabel: '', activeLabel: ''}));
			this._dataTable.setRows(this._rows);
			board.entryCount = this._rows.length;
			this.updateLimitHint();
			return;
		}

		if (!board.weightClassKey) {
			this._dataTable.setRows([]);
			this.updateLimitHint();
			return;
		}

		const rows = await ServerAPI.get<CatalogRow[]>(
			`/site/rankings/catalog?weightClassKey=${encodeURIComponent(board.weightClassKey)}`,
		);
		this._rows = rows.map(r => ({
			...r,
			championLabel: r.isChampion ? 'C' : '',
			activeLabel: r.active ? 'Yes' : 'No',
		}));
		this._dataTable.setRows(this._rows);
		board.entryCount = this._rows.filter(r => r.active).length;
		this.updateLimitHint();
	}

	showContextMenu(event: Event, row: RankRow, rowIndex: number) {
		const board = this._Board;
		if (!board || rowIndex < 0) {
			return;
		}
		const items: { Content: string; Value: string; FontImage?: string }[] = [];
		if (rowIndex > 0) {
			items.push({FontImage: NP.FontImages.arrow_upward, Content: 'Move up', Value: 'up'});
		}
		if (rowIndex < this._rows.length - 1) {
			items.push({FontImage: NP.FontImages.arrow_downward, Content: 'Move down', Value: 'down'});
		}
		if (!this.isPfpBoard() && 'catalogId' in row) {
			items.push({
				FontImage: NP.FontImages.edit,
				Content: row.isChampion ? 'Unset champion' : 'Set champion',
				Value: 'champion',
			});
			items.push({
				FontImage: NP.FontImages.edit,
				Content: row.active ? 'Deactivate' : 'Activate',
				Value: 'active',
			});
			if (row.active) {
				items.push({FontImage: NP.FontImages.delete, Content: 'Remove from board', Value: 'remove'});
			}
		}
		if (this.isPfpBoard() && 'entryId' in row) {
			items.push({FontImage: NP.FontImages.delete, Content: 'Remove from board', Value: 'remove'});
		}
		if (items.length === 0) {
			return;
		}
		const menu = new Menu({
			Items: items,
			EvItemSelected: async (sender: Menu, ev: { Item: ListItem }) => {
				const action = ev.Item.getValue();
				if (action === 'up' || action === 'down') {
					await this.moveRow(rowIndex, action === 'up' ? -1 : 1);
				} else if (action === 'champion' && 'catalogId' in row) {
					await ServerAPI.post<boolean>(
						`/site/rankings/catalog/${row.catalogId}/champion?isChampion=${!row.isChampion}`,
					);
					await this.refresh();
				} else if (action === 'active' && 'catalogId' in row) {
					await ServerAPI.post<boolean>(
						`/site/rankings/catalog/${row.catalogId}/active?active=${!row.active}`,
					);
					await this.refresh();
				} else if (action === 'remove' && 'entryId' in row) {
					this.confirmRemove(row.fullName, async () => {
						await ServerAPI.post<boolean>(`/site/rankings/board/${row.entryId}/remove`);
						await this.refresh();
					});
				} else if (action === 'remove' && 'catalogId' in row) {
					this.confirmRemove(row.fullName, async () => {
						await ServerAPI.post<boolean>(`/site/rankings/catalog/${row.catalogId}/remove`);
						await this.refresh();
					});
				}
			},
		});
		menu.showContextMenu(event);
	}

	confirmRemove(fighterName: string, onOk: () => Promise<void>) {
		NP.showConfirm({
			Content: `Remove ${fighterName} from this board?`,
			OkRK: 'Remove',
			EvOkButtonClick: async () => {
				try {
					await onOk();
				} catch (err) {
					NP.showMessage({Content: String(err)});
				}
			},
		});
	}

	async moveRow(rowIndex: number, delta: number) {
		const nextIndex = rowIndex + delta;
		if (nextIndex < 0 || nextIndex >= this._rows.length) {
			return;
		}
		await this.moveRowToIndex(this._rows[rowIndex], nextIndex);
	}

	async moveRowToIndex(row: RankRow, newIndex: number) {
		const board = this._Board;
		if (!board || this.isChampionsBoard()) {
			return;
		}
		const rowIndex = this.rowIndexOf(row);
		if (rowIndex < 0 || rowIndex === newIndex || newIndex < 0 || newIndex >= this._rows.length) {
			return;
		}
		const ordered = [...this._rows];
		const [item] = ordered.splice(rowIndex, 1);
		ordered.splice(newIndex, 0, item);
		try {
			if (this.isPfpBoard()) {
				const ids = ordered.map(r => (r as BoardRow).entryId);
				await ServerAPI.post<boolean>('/site/rankings/reorder', {
					boardSlug: board.slug,
					orderedIds: ids,
				});
			} else {
				const ids = ordered.map(r => (r as CatalogRow).catalogId);
				await ServerAPI.post<boolean>('/site/rankings/reorder', {
					weightClassKey: board.weightClassKey,
					orderedIds: ids,
				});
			}
			await this.refresh();
		} catch (err) {
			NP.showMessage({Content: String(err)});
			await this.refresh();
		}
	}

	addFighter() {
		const board = this._Board;
		if (!board || !NP.getCurrentUser().isBDAdmin()) {
			return;
		}
		if (this.isChampionsBoard()) {
			NP.showMessage({
				Content: 'Set Champion on a division board entry — champions list updates automatically.',
			});
			return;
		}
		let picker: Dialog;
		picker = new Dialog({
			AutoDestroy: true,
			Caption: 'Select athlete',
			ShowHeader: true,
			ShowCloseButton: true,
			ShowSearch: true,
			ShowButtons: false,
			Width: 640,
			Height: 520,
			Class: 'nopad',
			Content: new SimpleAthletesList({
				Mode: PageMode.mmaathletes,
				Width: 640,
				Height: 456,
				Sex: this.requiredBoardSex(),
				EvAthleteSelected: async (sender: object, item: IAthleteListItem) => {
					picker.hide();
					const athleteId = item.Athlete.Id;
					const athleteName = `${item.Athlete.FirstName || ''} ${item.Athlete.LastName || ''}`.trim();
					if (this.isBoardFull()) {
						this.showReplaceDialog(athleteId, athleteName);
						return;
					}
					await this.submitAddFighter(athleteId);
				},
			}),
			EvSearch: (sender: object, e: { Value: string }) => {
				(picker.getContent() as SimpleAthletesList).setFilter(e.Value);
			},
		});
		picker.show();
		(picker.getContent() as SimpleAthletesList).refresh();
	}

	getReplaceCandidates(): { label: string; replaceBoardEntryId?: number; replaceCatalogId?: number }[] {
		if (this.isPfpBoard()) {
			return (this._rows as BoardRow[]).map((row, idx) => ({
				label: `#${idx + 1} ${row.fullName}`,
				replaceBoardEntryId: row.entryId,
			}));
		}
		return (this._rows as CatalogRow[])
			.filter(row => row.active)
			.map(row => ({
				label: `${row.isChampion ? 'C' : row.displayOrder}. ${row.fullName}`,
				replaceCatalogId: row.catalogId,
			}));
	}

	showReplaceDialog(athleteId: number, athleteName: string) {
		const candidates = this.getReplaceCandidates();
		if (candidates.length === 0) {
			NP.showMessage({Content: 'No fighters on board to replace.'});
			return;
		}
		const board = this._Board;
		const contentNode = document.createElement('div');
		contentNode.className = 'site-rankings-replace-dialog';
		contentNode.style.padding = '20px 24px 12px';
		contentNode.style.boxSizing = 'border-box';
		const hint = document.createElement('p');
		hint.className = 'site-rankings-replace-hint';
		hint.style.margin = '0 0 20px';
		hint.style.lineHeight = '1.45';
		hint.textContent = `Add ${athleteName} — choose who leaves ${board.titleEn}:`;
		contentNode.appendChild(hint);
		let replaceSelect: Select;
		const dialog = new Dialog({
			AutoDestroy: true,
			Caption: 'Replace fighter',
			ShowHeader: true,
			ShowCloseButton: true,
			ShowButtons: true,
			DialogActionsCustomNode: true,
			DialogActionsNoShadow: true,
			Width: 560,
			Content: contentNode,
			EvOkButtonClick: async (sender: Dialog, e: { IsCancelClose?: boolean }) => {
				const value = replaceSelect.getValue();
				if (value == null || value === '') {
					NP.showMessage({Content: 'Select a fighter to replace.'});
					e.IsCancelClose = true;
					return;
				}
				const candidate = candidates.find(c =>
					this.isPfpBoard()
						? c.replaceBoardEntryId === value
						: c.replaceCatalogId === value,
				);
				await this.submitAddFighter(
					athleteId,
					candidate?.replaceBoardEntryId,
					candidate?.replaceCatalogId,
				);
			},
		});
		replaceSelect = new Select({
			ParentNode: contentNode,
			FloatingLabel: 'Replace',
			FullWidth: true,
			Width: 512,
		});
		replaceSelect.setItems(candidates.map(c => ({
			Content: c.label,
			Value: this.isPfpBoard() ? c.replaceBoardEntryId : c.replaceCatalogId,
		})));
		replaceSelect.setValue(
			this.isPfpBoard() ? candidates[0].replaceBoardEntryId : candidates[0].replaceCatalogId,
			false,
		);
		dialog.show();
		const okBtn = dialog.getOkButton();
		okBtn.setContent('Replace');
		okBtn.removeClass('dialog__action');
		okBtn.addClass('app-button--unelevated app-button--neutral');
	}

	async submitAddFighter(athleteId: number, replaceBoardEntryId?: number, replaceCatalogId?: number) {
		const board = this._Board;
		if (!board) {
			return;
		}
		try {
			const body: Record<string, unknown> = {
				athleteId,
			};
			if (this.isPfpBoard()) {
				body.boardSlug = board.slug;
				if (replaceBoardEntryId != null) {
					body.replaceBoardEntryId = replaceBoardEntryId;
				}
			} else {
				body.weightClassKey = board.weightClassKey;
				body.isChampion = false;
				if (replaceCatalogId != null) {
					body.replaceCatalogId = replaceCatalogId;
				}
			}
			await ServerAPI.post<number>('/site/rankings/add', body);
			await this.refresh();
		} catch (err) {
			NP.showMessage({Content: String(err)});
		}
	}
}
